/**
 * UsersOnlineManager (Frontend)
 *
 * PURPOSE
 * -------
 * Works with the backend `RedisUsersOnlineService` to maintain an accurate,
 * low-latency roster of which users are online for the current organization.
 *
 * HOW IT WORKS WITH THE BACKEND
 * -----------------------------
 * - Backend keeps a ZSET of (userId::channel) entries, lastSeenMs as the score.
 * - Each heartbeat from any source updates Redis atomically via Lua, pruning
 *   stale entries per configured channel windows.
 * - Backend exposes `API.Users.getOnlineUsers(orgId)` → Record<number, OnlineRosterEntry>.
 *   This is recomputed from the ZSET so each user has:
 *       userId
 *       lastSeenMs  (freshest across channels)
 *       online      (true if any channel's lastSeenMs is within its window)
 *       channels[]  (per-channel lastSeenMs, window, and online boolean)
 * - Backend broadcasts full snapshots via `API.Events.broadcastUsersOnlineUpdate`
 *   only when a user's online/offline state flips, rate-limited to once per org per 10 seconds.
 *
 * WHAT THIS CLASS DOES
 * --------------------
 * - Maintains a local `usersOnline` map in the `UsersModel` Zustand store.
 * - Recomputes `online` locally every 5 seconds (`reconcileIntervalMs`) using
 *   the same rules as the backend (per-channel windows from
 *   UserOnlineWindowByChannel and UserOnlineDefaultWindowMs).
 *   This allows the UI to flip a user offline locally without waiting
 *   for a backend broadcast or API fetch.
 * - Fetches a full snapshot from the backend every 10 seconds (`fetchIntervalMs`)
 *   and treats that as the source of truth, overwriting local state.
 
 *
 * WHY THIS APPROACH
 * -----------------
 * - Reduces load: backend only sends updates when there’s a flip, rate-limited.
 * - Keeps UI accurate in between server updates by recalculating locally.
 * - Avoids per-user timers: one bulk check every few seconds is enough.
 * - Matches backend’s logic exactly, so local and remote views stay consistent.
 
 */
import { API } from "@swivl-mono/api-helper";
import { OnlineRosterEntry, UserOnlineDefaultWindowMs, UserOnlineWindowByChannel } from "@swivl-mono/types";

import { Member } from "../Member/Member.Current";
import { UsersModel } from "./Users.Model";

const buffer = 10_000; // 1 second buffer to avoid flickering

export class UsersOnlineManager {
  static _usersOnlineCache: { [userId: number]: OnlineRosterEntry } = {};
  static _reconcileTimer?: number;
  static _fetchTimer?: number;
  static reconcileIntervalMs = 5_000;
  static fetchIntervalMs = 30_000;

  static isConversationFresh(userId?: number): boolean {
    if (!userId) {
      return false;
    }
    const entry = this._usersOnlineCache[userId];
    if (entry?.online) {
      return true;
    }
    const nowMs = Date.now();
    const conversations = UsersModel.state.conversations;

    if (conversations?.[userId]) {
      const conversation = conversations[userId];

      const lastUserMessageAt = conversation.user?.lastUserMessageAt;
      const timeSinceLastMessage = lastUserMessageAt?.getTime ? lastUserMessageAt?.getTime() : undefined;
      if (timeSinceLastMessage && nowMs - timeSinceLastMessage < UserOnlineDefaultWindowMs) {
        return true;
      }
      if (
        conversation.sessions &&
        Object.values(conversation.sessions)?.some(
          (s) =>
            (s.createdAt && s.createdAt.getTime && s.createdAt.getTime() > nowMs - UserOnlineDefaultWindowMs) ||
            (s.lastUserMessageAt && s.lastUserMessageAt?.getTime && s.lastUserMessageAt?.getTime() > nowMs - UserOnlineDefaultWindowMs),
        )
      ) {
        return true;
      }
    }
    return false;
  }

  static recomputeEntry(entry: OnlineRosterEntry, nowMs: number): OnlineRosterEntry {
    // const conversations = UsersModel.state.conversations;
    let newest = entry.lastSeenMs;
    let anyOnline = false;

    const channels = entry.channels.map((c) => {
      const w = UserOnlineWindowByChannel[c.channel] ?? UserOnlineDefaultWindowMs;
      const online = c.lastSeenMs >= nowMs - w - buffer;
      if (c.lastSeenMs > newest) newest = c.lastSeenMs;
      if (online) anyOnline = true;
      return { ...c, windowMs: w, online };
    });
    // if (conversations?.[entry.userId]) {
    //   // const conversation = conversations[entry.userId];
    //

    //   const lastUserMessageAt = conversation.user?.lastUserMessageAt;
    //   const timeSinceLastMessage = lastUserMessageAt?.getTime();
    //   if (timeSinceLastMessage && nowMs - timeSinceLastMessage < UserOnlineDefaultWindowMs) {
    //     anyOnline = true;
    //   }
    // if (
    //   conversation.sessions &&
    //   Object.values(conversation.sessions)?.some(
    //     (s) =>
    //       s.createdAt.getTime() > nowMs - UserOnlineDefaultWindowMs ||
    //       (s.lastUserMessageAt && s.lastUserMessageAt?.getTime() > nowMs - UserOnlineDefaultWindowMs),
    //   )
    // ) {
    //   anyOnline = true;
    // }
    // }
    return { userId: entry.userId, lastSeenMs: newest, online: anyOnline, channels };
  }

  static recomputeMap(map: Record<number, OnlineRosterEntry>, nowMs: number): Record<number, OnlineRosterEntry> {
    const out: Record<number, OnlineRosterEntry> = {};
    for (const key of Object.keys(map)) {
      const uid = Number(key);
      out[uid] = this.recomputeEntry(map[uid]!, nowMs);
    }
    return out;
  }

  static start() {
    if (this._reconcileTimer == null) {
      const id = window.setInterval(() => this.reconcileNow(), this.reconcileIntervalMs);
      this._reconcileTimer = id;
      // do an immediate reconcile
      this.reconcileNow();
    }
    if (this._fetchTimer == null) {
      const id = window.setInterval(() => void this.fetchNow(), this.fetchIntervalMs);
      this._fetchTimer = id;
      // do an immediate fetch
      void this.fetchNow();
    }
  }

  static stop() {
    if (this._reconcileTimer != null) {
      clearInterval(this._reconcileTimer);
      this._reconcileTimer = undefined;
    }
    if (this._fetchTimer != null) {
      clearInterval(this._fetchTimer);
      this._fetchTimer = undefined;
    }
  }

  static updateUsersOnline(snapshot) {
    const now = Date.now();
    const recomputed = this.recomputeMap(snapshot, now);
    this.update(recomputed);
  }

  static reconcileNow() {
    let map = this._usersOnlineCache;
    if (!map || Object.keys(map).length === 0) return;
    const now = Date.now();
    const recomputed = this.recomputeMap(map, now);
    this.update(recomputed);
  }

  private static update(usersOnline: { [userId: number]: OnlineRosterEntry }) {
    this._usersOnlineCache = usersOnline;
    const hasChanges = UsersOnlineManager.hasChanges(usersOnline);

    if (this.hasChanges(usersOnline)) {
      UsersModel.setUserSessionOrEscalation(undefined, undefined, undefined, undefined, usersOnline);
    }
  }

  static hasChanges(newState: Record<number, OnlineRosterEntry>): boolean {
    const oldState = UsersModel.state.usersOnline;
    if (!oldState || Object.keys(oldState).length !== Object.keys(newState).length) return true;

    for (const key of Object.keys(newState)) {
      const newEntry = newState[Number(key)];
      const oldEntry = oldState[Number(key)];
      if (!oldEntry || newEntry.online !== oldEntry.online) {
        return true;
      }
    }
    return false;
  }

  static async fetchNow() {
    const snapshot = await API.Users.getOnlineUsers(Member.orgId);

    this.updateUsersOnline(snapshot);
  }
}
